import { useMutation } from '@apollo/client'
import { Alert, Button, Form } from 'admin-web-ui'
import gql from 'graphql-tag'
import { Store } from 'rc-field-form/lib/interface'
import React, { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { Project } from '../../../api'
import { UserFormFieldData, UserFormFields } from '../UserFormFields'

const CREATE_USER = gql`
  mutation createUser(
    $email: ID!
    $firstName: String
    $lastName: String
    $groups: [String!]!
  ) {
    createUser(
      emailAddress: $email
      firstName: $firstName
      lastName: $lastName
      groups: $groups
    )
  }
`

export const apiUserExistsMessage =
  'An account with the given email already exists'
const apiUserExistsRE = new RegExp(apiUserExistsMessage)

export const userExistsMessage =
  'A user with this email already exists. Please use another email address.'

export const generalErrorMessage = 'An error occurred. Please try again.'

const FormFooter = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding-top: 24px;
`

const StyledButton = styled(Button)`
  width: 160px;
`

const CancelButton = styled(StyledButton)`
  margin-right: 16px;
`

interface FormData extends UserFormFieldData {}

interface Props {
  projects: Project[]
}

export const AddAccountUserForm = (props: Props): React.ReactElement => {
  const { projects } = props
  const navigate = useNavigate()
  const [addUser] = useMutation(CREATE_USER)

  const [submitState, handleSubmit] = useAsyncFn(
    async (values?: Store) => {
      if (!values) {
        return undefined
      }

      const formData = values as FormData

      const groups = [...(formData.customerGroups ?? [])]
      for (const project of projects) {
        const projectGroups = formData[`projectGroups-${project.id}`]
        groups.push(...(projectGroups ?? []))
      }

      await addUser({
        variables: {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          groups,
        },
      })

      navigate('../account-users')
    },
    [addUser, history],
  )

  const emailSubmitError = useMemo(() => {
    if (submitState.error && apiUserExistsRE.test(submitState.error.message)) {
      return userExistsMessage
    }

    return undefined
  }, [submitState.error])

  const resetSubmitError = useCallback(() => {
    handleSubmit(undefined)
  }, [handleSubmit])

  return (
    <Form onFinish={handleSubmit} onChange={resetSubmitError}>
      <UserFormFields projects={projects} emailSubmitError={emailSubmitError} />

      {submitState.error && (
        <Alert type="error" message={emailSubmitError ?? generalErrorMessage} />
      )}

      <FormFooter>
        <Link to="../account-users">
          <CancelButton
            type="default"
            shape="round"
            disabled={submitState.loading}
          >
            Cancel
          </CancelButton>
        </Link>
        <StyledButton
          loading={submitState.loading}
          type="primary"
          shape="round"
          htmlType="submit"
        >
          Add User
        </StyledButton>
      </FormFooter>
    </Form>
  )
}
